import { format } from "date-fns";
import { PlusIcon } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import type {
  BookingSlot,
  PublicRoom,
  TimetableItem,
} from "./types";
import {
  GRID_ROW_MINUTES,
  TIME_START_HOUR,
  addMinutesToTime,
  formatTimeDisplay,
  getEventBlockAccentClass,
  getEventCellClass,
  getEventSlotSpan,
  getTimeSlotGroups,
  slotIsEventStart,
  timeToMinutes,
} from "./timetable-utils";

const ROW_HEIGHT_PX = 25;

type PublicTimetableDayViewProps = {
  date: string;
  items: TimetableItem[];
  rooms: PublicRoom[];
  onSlotSelect: (slot: BookingSlot) => void;
  onEventClick?: (item: TimetableItem) => void;
  conflictIds?: Set<string>;
  showCurrentTime?: boolean;
};

const EVENT_MODE_VIRTUAL_ROOM_ID = "__events__";

function getTypeLabel(type: TimetableItem["type"]): string {
  switch (type) {
    case "class":
      return "ຄລາດສ໌";
    case "private":
      return "ວິຊາສ່ວນຕົວ";
    case "room_booking":
      return "ຈອງຫ້ອງ";
    case "event":
      return "ກິດຈະກຳ";
    case "project_task":
      return "ວັນສົ່ງ Task";
    case "meeting_action":
      return "ປະເດັນທີ່ລົມກັນ";
    default:
      return "";
  }
}

export function PublicTimetableDayView({
  date,
  items,
  rooms,
  onSlotSelect,
  onEventClick,
  conflictIds,
  showCurrentTime = true,
}: PublicTimetableDayViewProps) {
  const timeSlotGroups = useMemo(() => getTimeSlotGroups(), []);

  /** ໂໝດປະຕິທິນວຽກ: ບໍ່ມີຫ້ອງ → ໃຊ້ຄໍເລັກດຽວ "ກິດຈະກຳ" */
  const effectiveRooms = useMemo(() => {
    if (rooms.length > 0) return rooms;
    return [{ id: EVENT_MODE_VIRTUAL_ROOM_ID, name: "ກິດຈະກຳ" }];
  }, [rooms]);

  const itemsByRoom = useMemo(() => {
    const map = new Map<string, TimetableItem[]>();
    const roomKey = (item: TimetableItem) =>
      item.roomId ?? EVENT_MODE_VIRTUAL_ROOM_ID;
    for (const item of items) {
      const key = roomKey(item);
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    }
    return map;
  }, [items]);

  const [now, setNow] = useState(new Date());
  const isToday = date === format(now, "yyyy-MM-dd");

  useEffect(() => {
    if (!showCurrentTime || !isToday) return;
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, [showCurrentTime, isToday]);

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const currentSeconds = now.getSeconds();
  const dayStartMinutes = TIME_START_HOUR * 60;
  const dayEndMinutes = 21 * 60;
  const showLine =
    showCurrentTime &&
    isToday &&
    currentMinutes >= dayStartMinutes &&
    currentMinutes < dayEndMinutes;

  // Calculate top position for floating current time line
  // Add ~40px offset for thead height
  const THEAD_HEIGHT = 40;
  const timeLineTop =
    showLine && currentMinutes >= dayStartMinutes
      ? THEAD_HEIGHT +
        ((currentMinutes - dayStartMinutes) / GRID_ROW_MINUTES) *
          ROW_HEIGHT_PX +
        (currentSeconds / 60 / GRID_ROW_MINUTES) * ROW_HEIGHT_PX
      : null;

  return (
    <div className="relative overflow-auto">
      {showLine && timeLineTop !== null && (
        <div
          className="pointer-events-none absolute right-0 left-0 z-10 flex items-center gap-2 px-2"
          style={{ top: `${timeLineTop}px` }}
        >
          <span className="w-16 shrink-0 text-left font-medium text-primary text-xs">
            {format(now, "HH:mm")}
          </span>
          <div className="h-0.5 flex-1 bg-primary" />
        </div>
      )}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-20 border-r border-b bg-muted/50 p-2 text-left font-medium text-muted-foreground">
              ເວລາ
            </th>
            {effectiveRooms.map((room) => (
              <th
                key={room.id}
                className="min-w-[140px] border-r border-b bg-muted/50 p-2 text-left font-medium last:border-r-0"
              >
                {room.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="relative">
          {timeSlotGroups.map(([slot1, slot2]) => {
            const slot1End = addMinutesToTime(slot1, GRID_ROW_MINUTES);
            const slot2End = addMinutesToTime(slot2, GRID_ROW_MINUTES);
            const renderRoomCells = (slotLabel: string, slotEnd: string) =>
              effectiveRooms.map((room) => {
                const roomItems = itemsByRoom.get(room.id) ?? [];
                const slotStartMin = timeToMinutes(slotLabel);
                const slotEndMin = timeToMinutes(slotEnd);
                const eventStartingHere = roomItems.find((item) =>
                  slotIsEventStart(item, slotLabel, slotEnd),
                );
                const eventCoveringHere = roomItems.find(
                  (item) =>
                    timeToMinutes(item.startTime) < slotEndMin &&
                    timeToMinutes(item.endTime) > slotStartMin &&
                    !slotIsEventStart(item, slotLabel, slotEnd),
                );

                if (eventCoveringHere) return null;

                if (eventStartingHere) {
                  const span = getEventSlotSpan(eventStartingHere);
                  return (
                    <td
                      key={room.id}
                      rowSpan={span}
                      className={`border-border border-r p-0 align-top last:border-r-0 ${getEventCellClass(eventStartingHere, conflictIds)} ${onEventClick ? "cursor-pointer hover:opacity-90" : ""}`}
                      onClick={() => onEventClick?.(eventStartingHere)}
                      onKeyDown={(e) => {
                        if (
                          onEventClick &&
                          (e.key === "Enter" || e.key === " ")
                        ) {
                          e.preventDefault();
                          onEventClick(eventStartingHere);
                        }
                      }}
                      role={onEventClick ? "button" : undefined}
                      tabIndex={onEventClick ? 0 : undefined}
                    >
                      <div
                        className={`flex h-full flex-col justify-between bg-background py-1.5 pr-2 pl-2 ${getEventBlockAccentClass(eventStartingHere.type, conflictIds, eventStartingHere)}`}
                        style={{
                          minHeight: ROW_HEIGHT_PX * span,
                        }}
                      >
                        <div className="text-muted-foreground text-xs">
                          {getTypeLabel(eventStartingHere.type)}
                        </div>
                        <div className="font-medium leading-tight">
                          {eventStartingHere.title}
                        </div>
                        <div className="whitespace-nowrap text-muted-foreground text-xs">
                          {formatTimeDisplay(eventStartingHere.startTime)}
                          {" – "}
                          {formatTimeDisplay(eventStartingHere.endTime)}
                        </div>
                      </div>
                    </td>
                  );
                }

                const startTimeStr =
                  slotLabel.length === 5 ? `${slotLabel}:00` : slotLabel;
                const endTimeStr =
                  slotEnd.length === 5 ? `${slotEnd}:00` : slotEnd;
                return (
                  <td
                    key={room.id}
                    className="border-border border-r align-top last:border-r-0"
                  >
                    <button
                      type="button"
                      className="flex h-full min-h-[24px] w-full flex-col items-center justify-center gap-0.5 bg-muted/20 text-muted-foreground transition-colors hover:border-primary/50 hover:bg-muted/40 hover:text-foreground"
                      onClick={() =>
                        onSlotSelect({
                          ...(room.id !== EVENT_MODE_VIRTUAL_ROOM_ID && {
                            roomId: room.id,
                            roomName: room.name,
                          }),
                          date,
                          startTime: startTimeStr,
                          endTime: endTimeStr,
                        })
                      }
                    >
                      <PlusIcon className="size-3" />
                      {/* <span className="text-[10px]">ຈອງ ({slotLabel})</span> */}
                    </button>
                  </td>
                );
              });

            return (
              <Fragment key={slot1}>
                <tr
                  className="border-border border-b"
                  style={{ height: ROW_HEIGHT_PX }}
                >
                  <td
                    rowSpan={2}
                    className="w-20 border-border border-r p-1 align-top text-muted-foreground text-xs"
                  >
                    {slot1}
                  </td>
                  {renderRoomCells(slot1, slot1End)}
                </tr>
                <tr
                  className="border-border border-b"
                  style={{ height: ROW_HEIGHT_PX }}
                >
                  {renderRoomCells(slot2, slot2End)}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
